import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import View from '@play-co/timestep-core/lib/ui/View';
import { Feature } from 'src/replicant/analytics';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import { AsyncScroll } from 'src/game/components/shared/AsyncScroll';
import {
  TeamEventData,
  TeamCompetitor,
} from 'src/replicant/team/competition/teamCompetition.ruleset';
import { TeamCompetitorEntry } from '../components/TeamCompetitorEntry';

import {
  getCurrentTeamCompetition,
  getUnclaimedTeamCompetition,
} from '../team.sequence';
import { TeamChestHeader } from '../components/TeamChestHeader';

import { AssetGroup } from '@play-co/timestep-core';

const ui = uiConfig.buruburu.care.confirmation;

export default class PopupTeamChestLeaderboard extends PopupBasic {
  private header: { update: (data?: TeamEventData) => void };
  private members: { update: (data?: TeamEventData) => void };
  private close: (result?: boolean) => void;

  constructor(opts: { superview: View; close: (result?: boolean) => void }) {
    super({
      superview: opts.superview,
      close: () => {
        opts.close(false);
      },
      ...ui.popup,
      width: 634,
      height: 1100,
      skipMessage: true,
      darkerBg: true,
      tracking: {
        feature: Feature.MATCH2.TEAM.CHEST._,
        $subFeature: Feature.MATCH2.TEAM.CHEST.LEADERBOARD_POPUP,
      },
    });
    this.title.root.removeFromSuperview();
    this.buttonClose.updateOpts({ width: 72, height: 76, x: 610, y: 15 });
    this.header = this.createHeader(this.box);
    this.members = this.createMembers(this.box);
    this.close = opts.close;
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/match2/team/frames/panel_list_beige_inset.png',
      'assets/ui/match2/team/chest/team_chest_header_bg.png',
      'assets/ui/match2/team/chest/team_chest_ribbon.png',
      'assets/ui/match2/team/chest/team_chest_pet1_happy.png',
      'assets/ui/match2/team/chest/team_chest_pet2_happy.png',
      'assets/ui/match2/team/chest/team_chest_pet2_sad.png',
      'assets/ui/match2/common/list_entry.png',
      'assets/ui/match2/team/competition/comp_score_frame.png',
      'assets/ui/match2/team/competition/comp_token.png',
      'assets/ui/match2/team/frames/comp_sticky_panel.png',
      'assets/ui/match2/team/frames/comp_panel_me.png',
    ]);
  }

  private createHeader(superview: View) {
    const header = new TeamChestHeader({ superview });
    header.updateOpts({
      superview,
      x: (superview.style.width - header.style.width) * 0.5,
      y: 20,
    });
    header.onOpenChest = () => {
      this.close(true);
    };
    return {
      update: (data?: TeamEventData) => {
        if (data) {
          header.update(data);
          return;
        }
        this.getCompetitionData().then((data) => {
          if (data) header.update(data);
        });
      },
    };
  }

  private createMembers(superview: View) {
    const { width, height } = { width: 590, height: 550 };

    const content = new View({
      tag: 'membersList',
      superview,
      width: width,
      height: height,
      x: (superview.style.width - width) * 0.5,
      y: superview.style.height - height - 20,
    });

    const bg = new ImageScaleView({
      tag: 'membersListBg',
      superview: content,
      image: 'assets/ui/match2/team/frames/panel_list_beige_inset.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 39, right: 39 },
        vertical: { top: 39, bottom: 39 },
      },
      width,
      height,
    });

    const scroll = new AsyncScroll<TeamCompetitor, TeamCompetitorEntry>({
      superview: bg,
      rect: {
        x: 0,
        y: 0,
        width: bg.style.width,
        height: bg.style.height - 10,
      },
      margin: 2,
      createItem: (superview, _, data) => {
        const entry = new TeamCompetitorEntry({
          superview,
          width: bg.style.width * 0.98,
          height: 90,
        });
        entry.style.x = (superview.style.width - entry.style.width) * 0.5;
        if ('loading' in data) return entry;
        entry.setup(data);
        return entry;
      },
      recycle: true,
      setData: (_, data, view) => {
        if ('loading' in data) return;
        view.setup(data);
      },
    });

    const stickyView = new TeamCompetitorEntry({
      x: 0,
      width: bg.style.width,
      height: 95,
    });

    stickyView.updateOpts({
      height: stickyView.style.height - 20,
      offsetY: -10,
    });

    return {
      update: (data?: TeamEventData) => {
        const dataProvider = async () => {
          if (data) return Promise.resolve(data);

          const unclaimed = await getUnclaimedTeamCompetition()?.get();
          const unclaimedChestEvent = unclaimed?.find(
            (item) => item.type === 'chest',
          );

          if (unclaimedChestEvent) {
            return unclaimedChestEvent;
          }

          const competition = getCurrentTeamCompetition()?.get();
          return competition;
        };
        scroll.clearStickyItem();
        scroll.setDataProvider(
          async () => {
            const competition = await dataProvider();
            if (!competition) return [];
            const player = competition.myTeam.find((player) => player.current);
            if (player) {
              scroll.setStickyItem(stickyView, player.position - 1);
              stickyView.setup({ ...player, alt: true });
            }
            return competition.myTeam;
          },
          {
            feature: Feature.TEAM._,
            subFeature: Feature.TEAM.CHEST_LEADERBOARD,
          },
        );
      },
    };
  }

  public init({ data }: { data?: TeamEventData }): void {
    super.init({
      title: i18n('match2.team.info_popup.title'),
    });

    this.header.update(data);
    this.members.update(data);
  }

  private async getCompetitionData() {
    const unclaimed = await getUnclaimedTeamCompetition()?.get();
    const unclaimedChestEvent = unclaimed?.find(
      (item) => item.type === 'chest',
    );

    if (unclaimedChestEvent) {
      return unclaimedChestEvent;
    }

    const competition = getCurrentTeamCompetition()?.get();
    return competition;
  }
}
